import { ModalConfirm } from '@/components/element/modal/ModalConfirm';
import { ModalMessage } from '@/components/element/modal/ModalMessage';
import { MotionDiv } from '@/components/element/MotionDiv';
import { SimpleTextFormat, SimpleTextFormatWithLink } from '@/components/element/SimpleTextFormat';
import { useTeamSalesOrderPhases } from '@/hooks/api/0250_team_sales_order_phases';
import { useDestroySalesOrder } from '@/hooks/api/1240_destroy_sales_order';
import { useArchiveSalesOrder } from '@/hooks/api/1250_archive_sales_order';
import { useUnarchiveSalesOrder } from '@/hooks/api/1251_unarchive_sales_order';
import { useSalesOrderInvalidate } from '@/hooks/api/1260_sales_order';
import { useUpdateSalesOrderPhase } from '@/hooks/api/1270_update_sales_order_phase';
import { SalesOrderLogic } from '@/hooks/api/models/logics';
import { toastApiError, toastSuccess } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentSalesOrder } from '@/providers/SalesOrderProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { dateFormat } from '@/utils';
import React, { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BusinessPartnerCreateForm } from '@/components/features/BusinessPartnerCreateForm';
import { useCreateSalesOrderBoardProject } from '@/hooks/api/2500_create_sales_order_board_project';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormGroup } from '@/components/element/Form/FormGroup';
import { useWorkspaceInvalidate } from '@/hooks/api/0101_workspace';
import { useQuotationTasksInvalidate } from '@/hooks/api/1440_quotation_tasks';
import { useDestroySalesOrderBoardProject } from '@/hooks/api/2510_destroy_sales_order_board_project';
import { useSyncBoardProjectName } from '@/hooks/api/2610_sync_board_project_name';

export const ProjectSummary = () => {
  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me } = useCurrentTeamMember();
  const { _so_ } = useCurrentSalesOrder();
  const { mutate, isPending, isSuccess, error } = useDestroySalesOrder();

  useEffect(() => setShowLoading(isPending), [isPending]);

  useEffect(() => {
    if (isSuccess) {
      toastSuccess('プロジェクト（売上）を削除しました');
      navigate(`/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders`);
    }
  }, [isSuccess]);

  useEffect(() => toastApiError(error), [error]);

  const onDeleteClick = () => {
    mutate({ identifier: _w_!.identifier, team_id: _t_!.id, sales_order_id: _so_!.id });
  };

  return (
    <MotionDiv className="card mb-4">
      <div className="card-header d-flex align-items-center justify-content-between">
        <div>
          <h6 className="mb-0">プロジェクト基本情報</h6>
        </div>
        <div className="d-flex align-items-center">
          <NewSalesOrderFromCopyLink />
          <Link
            to={`/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders/${_so_!.id}/edit`}
            className="btn btn-outline-success btn-icon-only"
          >
            <i className="fas fa-edit"></i>
          </Link>
          <div className="dropstart ms-3">
            <a href="" className="text-secondary" id="dropdownMarketingCard" data-bs-toggle="dropdown">
              <i className="fas fa-ellipsis-v"></i>
            </a>
            <ul className="dropdown-menu dropdown-menu-lg-start px-2 py-3" aria-labelledby="dropdownMarketingCard">
              <li>{_so_!.archived ? <UnarchiveProjectLink /> : <ArchiveProjectLink />}</li>
              <li>
                <button
                  data-bs-toggle="modal"
                  data-bs-target={`#delConfirm`}
                  className="dropdown-item border-radius-md text-danger"
                >
                  <i className="fas fa-trash-alt me-2"></i>プロジェクトを削除
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="card-body pt-0">
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">プロジェクト名</div>
          <div className="font-weight-bold text-end px-0 text-sm text-dark">{_so_!.name}</div>
        </div>
        <hr className="horizontal dark" />
        {_so_!.archived && (
          <>
            <div className="d-flex align-items-center justify-content-between">
              <div className="text-secondary text-xs font-weight-bold pe-2">アーカイブ</div>
              <div className="font-weight-bold text-end px-0 text-sm text-dark">
                <span className="badge badge-danger">アーカイブ済</span>
              </div>
            </div>
            <hr className="horizontal dark" />
          </>
        )}
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">フェーズ</div>
          <div className="font-weight-bold text-end px-0 d-flex align-items-stretch">
            <PhasesSelect />
          </div>
        </div>
        <hr className="horizontal dark" />
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">プロジェクト担当者</div>
          <div className="d-flex align-items-center">
            <img src={_so_!.user.user_account.icon_file_url} className="avatar avatar-xs rounded-circle me-1" />
            <div className="font-weight-bold px-0 text-sm text-dark">{_so_!.user.user_account.name}</div>
          </div>
        </div>
        <hr className="horizontal dark" />
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">関連メンバー</div>
          <div className="d-flex align-items-center justify-content-center">
            <div className="avatar-group text-nowrap overflow-hidden flex-1">
              {_so_!.sales_order_members.map((m) => (
                <img
                  key={`so-m-img-${m.id}`}
                  src={m.user.user_account.icon_file_url}
                  className="avatar avatar-xs rounded-circle"
                />
              ))}
            </div>
            {SalesOrderLogic.canUpdate(_t_!, _so_!, me!) && (
              <Link
                to={`/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders/${_so_!.id}/sales_order_members`}
                className="btn btn-sm bg-gradient-secondary ms-2 px-3"
              >
                詳細
              </Link>
            )}
          </div>
        </div>
        <hr className="horizontal dark" />
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">取引先</div>
          <div className="font-weight-bold text-end px-0 text-sm text-dark">{_so_!.business_partner.display_name}</div>
        </div>
        <hr className="horizontal dark" />
        {_so_!.agency && (
          <>
            <div className="d-flex align-items-center justify-content-between">
              <div className="text-secondary text-xs font-weight-bold pe-2">代理店</div>
              <div className="font-weight-bold text-end px-0 text-sm text-dark">{_so_!.agency.display_name}</div>
            </div>
            <hr className="horizontal dark" />
          </>
        )}
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">取引先担当者</div>
          <div className="font-weight-bold text-end px-0 text-sm text-dark">{_so_!.person_in_charge}</div>
        </div>
        <hr className="horizontal dark" />
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">
            売上（税抜）
            {_so_!.accounting_manual_mode && <> - 手動管理</>}
          </div>
          <div className="d-flex align-items-center">
            <div className="font-weight-bold text-end px-0 text-sm text-dark">
              ¥{_so_!.accounting_total_amount.toLocaleString()}
            </div>
            <BillingSchedulesLink />
          </div>
        </div>
        <hr className="horizontal dark" />
        {_so_!.spot_billing_date && (
          <>
            <div className="d-flex align-items-center justify-content-between">
              <div className="text-secondary text-xs font-weight-bold pe-2">請求日</div>
              <div className="font-weight-bold text-end px-0 text-sm text-dark">
                {dateFormat({ date: _so_!.spot_billing_date })}
              </div>
            </div>
            <hr className="horizontal dark" />
          </>
        )}
        {(_so_!.contract_start_date || _so_!.contract_end_date) && (
          <>
            <div className="d-flex align-items-center justify-content-between">
              <div className="text-secondary text-xs font-weight-bold pe-2">契約期間</div>
              <div className="font-weight-bold text-end px-0 text-sm text-dark">
                {_so_!.contract_start_date ? dateFormat({ date: _so_!.contract_start_date }) : ''} -{' '}
                {_so_!.contract_end_date ? dateFormat({ date: _so_!.contract_end_date }) : ''}
              </div>
            </div>
            <hr className="horizontal dark" />
          </>
        )}
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">売上カテゴリ</div>
          <div className="font-weight-bold text-end px-0 text-sm text-dark">{_so_!.product.name}</div>
        </div>
        <hr className="horizontal dark" />
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">メモ</div>
          <div className="px-0 text-xs break-word">
            <SimpleTextFormatWithLink content={_so_!.description || ''} />
          </div>
        </div>
        <hr className="horizontal dark" />
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">請求タスク処理マニュアル</div>
          <div className="px-0 text-xs break-word">
            <SimpleTextFormatWithLink content={_so_!.task_procedure_manual || ''} />
          </div>
        </div>
        {_w_!.board_setting && (
          <>
            <hr className="horizontal dark" />
            <div className="d-flex align-items-center justify-content-between">
              <div className="text-secondary text-xs font-weight-bold pe-2">board連携</div>
              <div className="font-weight-bold text-end px-0 text-sm text-dark"></div>
              <div className="px-0 text-xs break-word">
                {_so_!.sales_order_board_project ? (
                  <>
                    <Link
                      to={_so_!.sales_order_board_project.url}
                      className="font-weight-bold text-end p-0 text-sm pe-2 btn btn-link"
                      target="_blank"
                    >
                      <u>{_so_!.sales_order_board_project.name}</u>
                    </Link>

                    <BoardDisconnectButton />
                  </>
                ) : (
                  <BoardConnectButton />
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <ModalConfirm modalId={'delConfirm'} okClicked={onDeleteClick} message={'削除します。よろしいですか？'} />
    </MotionDiv>
  );
};

// コピーして作成ボタン
const NewSalesOrderFromCopyLink = () => {
  const navigate = useNavigate();
  const { _w_, _t_ } = useCurrentTeamMember();
  const { _so_ } = useCurrentSalesOrder();

  const handleOkClick = () => {
    navigate(`/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders/new`, { state: { copyFrom: _so_! } });
  };

  return (
    <>
      <ModalConfirm
        modalId={'copySalesOrderConfirm'}
        okClicked={handleOkClick}
        message={
          '既存のプロジェクト情報をコピーして\n新たなプロジェクトを作成します。\n\n期間や金額の設定内容に注意してください。'
        }
      />

      <button
        className="btn btn-outline-info btn-icon-only me-1"
        data-bs-toggle="modal"
        data-bs-target={`#copySalesOrderConfirm`}
      >
        <i className="fas fa-copy"></i>
      </button>
    </>
  );
};

// アーカイブボタン
const ArchiveProjectLink = () => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_ } = useCurrentTeamMember();
  const { _so_ } = useCurrentSalesOrder();
  const { mutate, isPending, isSuccess, error } = useArchiveSalesOrder();
  const { invalidate } = useSalesOrderInvalidate({ identifier: _w_!.identifier, team_id: _t_!.id, id: _so_!.id });

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (isSuccess) {
      toastSuccess('アーカイブしました');
      invalidate();
    }
  }, [isSuccess]);

  const onArchiveClick = () => {
    mutate({ identifier: _w_!.identifier, team_id: _t_!.id, id: _so_!.id });
  };

  return (
    <button className="dropdown-item border-radius-md" onClick={onArchiveClick}>
      <i className="fas fa-archive me-2"></i>プロジェクトをアーカイブ
    </button>
  );
};

// アーカイブ解除ボタン
const UnarchiveProjectLink = () => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_ } = useCurrentTeamMember();
  const { _so_ } = useCurrentSalesOrder();
  const { mutate, isPending, isSuccess, error } = useUnarchiveSalesOrder();
  const { invalidate } = useSalesOrderInvalidate({ identifier: _w_!.identifier, team_id: _t_!.id, id: _so_!.id });

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (isSuccess) {
      toastSuccess('アーカイブを解除しました');
      invalidate();
    }
  }, [isSuccess]);

  const onArchiveClick = () => {
    mutate({ identifier: _w_!.identifier, team_id: _t_!.id, id: _so_!.id });
  };

  return (
    <button className="dropdown-item border-radius-md" onClick={onArchiveClick}>
      <i className="fas fa-box-open me-2"></i>アーカイブを解除
    </button>
  );
};

// フェーズ
const PhasesSelect = () => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_ } = useCurrentTeamMember();
  const { _so_ } = useCurrentSalesOrder();
  const { invalidate } = useSalesOrderInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _so_!.id,
  });
  const { data: phases } = useTeamSalesOrderPhases({ identifier: _w_!.identifier, team_id: _t_!.id });
  const { mutate, data, isPending, error } = useUpdateSalesOrderPhase();
  const showMessageButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  const onPhaseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    mutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      sales_order_id: _so_!.id,
      team_sales_order_phase_id: Number(e.target.value),
    });
  };

  useEffect(() => {
    if (data) {
      invalidate();

      if ('契約未承認' == data.alert) {
        // フェーズ移動でアラートが設定された場合にモーダルでメッセージ表示
        showMessageButtonRef.current?.click();
      }
    }
  }, [data]);

  return (
    <>
      <ModalMessage
        modalId="messageModal"
        message="承認された契約タスクがありません"
        showModalButtonRef={showMessageButtonRef}
      />
      <select
        className="form-control form-control-sm"
        value={_so_!.team_sales_order_phase.id}
        onChange={(e) => onPhaseChange(e)}
      >
        {phases?.map((phase) => (
          <option key={`so-phase-${phase.id}`} value={phase.id}>
            {phase.label}
          </option>
        ))}
      </select>
    </>
  );
};

// 請求時期ボタン
const BillingSchedulesLink = () => {
  const { _w_, _t_ } = useCurrentTeamMember();
  const { _so_ } = useCurrentSalesOrder();

  return (
    <Link
      to={`/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders/${_so_!!.id}/billing_schedules`}
      className="btn btn-sm bg-gradient-secondary ms-2 px-3"
    >
      詳細
    </Link>
  );
};

//
// board連携
//
type BoardConnectFormInputs = {
  identifier: string;
  team_id: number;
  sales_order_id: number;
  url: string;
};

const BoardConnectButton = () => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_ } = useCurrentTeamMember();
  const { _so_ } = useCurrentSalesOrder();
  const { mutate, isPending, data, error } = useCreateSalesOrderBoardProject();
  const { invalidate: salesOrderInvalidate } = useSalesOrderInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _so_!.id,
  });
  const { invalidate: invalidateQuotationTasks } = useQuotationTasksInvalidate(_w_!.identifier);

  const {
    register,
    handleSubmit,
    resetField,
    formState: { errors },
  } = useForm<BoardConnectFormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: { identifier: _w_!.identifier, team_id: _t_!.id, sales_order_id: _so_!.id, url: '' },
  });

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (data) {
      salesOrderInvalidate();
      invalidateQuotationTasks();
      setShowLoading(false);
      resetField('url');
      document.getElementById('modal-board-connect-close')?.click();
    }
  }, [data]);

  const onSubmit: SubmitHandler<BoardConnectFormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  return (
    <>
      <Link
        to={``}
        className="btn bg-gradient-primary btn-icon"
        data-bs-toggle="modal"
        data-bs-target={`#modal-board-connect`}
      >
        <i className="fas fa-plus-circle"></i>連携
      </Link>

      <div
        className="modal fade"
        id="modal-board-connect"
        role="dialog"
        aria-labelledby="modal-form"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-content">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title">board連携</h5>
                <button type="button" className="btn-close text-dark" data-bs-dismiss="modal" aria-label="Close">
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-header pt-2">
                <p className="text-xs mb-0">boardの案件ページのURLを入力してください。</p>
                <p className="text-xs mb-0"></p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="modal-body">
                  <FormGroup label={'board案件URL'} required={true}>
                    <input
                      type="text"
                      className={`form-control ${errors.url && 'is-invalid'}`}
                      {...register('url', { required: '入力して下さい。' })}
                    />
                    {errors.url && <div className="text-xs text-danger mt-1">{errors.url.message}</div>}
                  </FormGroup>
                </div>
                <div className="modal-footer">
                  <button
                    id="modal-board-connect-close"
                    type="button"
                    className="btn btn-outline-secondary modal-close-btn"
                    data-bs-dismiss="modal"
                  >
                    Close
                  </button>
                  <button type="submit" className="btn bg-gradient-dark btn-icon">
                    <i className="fas fa-check-circle"></i>新規登録する
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const BoardDisconnectButton = () => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_ } = useCurrentTeamMember();
  const { _so_ } = useCurrentSalesOrder();
  const {
    mutate: mutateDestroy,
    isPending: isPendingDestroy,
    data: dataDestroy,
    error: errorDestroy,
  } = useDestroySalesOrderBoardProject();
  const {
    mutate: mutateSyncName,
    isPending: isPendingSyncName,
    data: dataSyncName,
    error: errorSyncName,
  } = useSyncBoardProjectName();
  const { invalidate: salesOrderInvalidate } = useSalesOrderInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _so_!.id,
  });
  const { invalidate: invalidateQuotationTasks } = useQuotationTasksInvalidate(_w_!.identifier);

  useEffect(() => setShowLoading(isPendingDestroy || isPendingSyncName), [isPendingDestroy, isPendingSyncName]);
  useEffect(() => toastApiError(errorDestroy || errorSyncName), [errorDestroy, errorSyncName]);

  useEffect(() => {
    if (dataDestroy) {
      toastSuccess('board連携を解除しました');
      salesOrderInvalidate();
      invalidateQuotationTasks();
      document.getElementById('modal-board-connect-close')?.click();
    }
  }, [dataDestroy]);

  useEffect(() => {
    if (dataSyncName) {
      toastSuccess('案件名再取得が完了しました');
      salesOrderInvalidate();
      invalidateQuotationTasks();
      document.getElementById('modal-board-connect-close')?.click();
    }
  }, [dataSyncName]);

  const onDeleteClick = () => {
    mutateDestroy({ identifier: _w_!.identifier, team_id: _t_!.id, sales_order_id: _so_!.id });
  };

  const onSyncBoardProjectName = () => {
    mutateSyncName({ identifier: _w_!.identifier, team_id: _t_!.id, sales_order_id: _so_!.id });
  };

  return (
    <>
      <Link
        to={``}
        className="btn btn-sm bg-gradient-secondary ms-2 px-3"
        data-bs-toggle="modal"
        data-bs-target={`#modal-board-detail`}
      >
        詳細
      </Link>

      <ModalConfirm
        modalId={'modal-board-disconnect'}
        okClicked={onDeleteClick}
        message={'連携を解除します。よろしいですか？'}
      />

      <div>
        <div
          className="modal fade"
          id="modal-board-detail"
          tabIndex={-1}
          role="dialog"
          aria-labelledby="modal-board-detail"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-danger modal-dialog-centered modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title">board連携詳細</h5>
                <button type="button" className="btn-close text-dark" data-bs-dismiss="modal" aria-label="Close">
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-header pt-2">
                <p className="text-xs mb-0">
                  board案件名：
                  <span className="text-sm font-weight-bold">【{_so_!.sales_order_board_project!.name}】</span>
                </p>
                <p className="text-xs mb-0"></p>
              </div>

              <div className="modal-body">
                <FormGroup label={'board案件URL'} required={true}>
                  <input
                    type="text"
                    className={`form-control`}
                    value={_so_!.sales_order_board_project!.url}
                    disabled={true}
                  />
                </FormGroup>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn bg-gradient-secondary btn-sm" data-bs-dismiss="modal">
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn bg-gradient-primary btn-sm"
                  data-bs-dismiss="modal"
                  onClick={() => onSyncBoardProjectName()}
                >
                  案件名再取得
                </button>
                <button
                  type="button"
                  className="btn bg-gradient-danger btn-sm btn-icon"
                  data-bs-toggle="modal"
                  data-bs-target={`#modal-board-disconnect`}
                >
                  <i className="fas fa-minus-circle"></i>連携解除
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
